import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, MailCheck } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import PhoneInput from "@/components/PhoneInput";
import { getVisitorId } from "@/lib/analytics";
import { adminPath } from "@/lib/adminBase";
import { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useSEO } from "@/lib/seo";
import api from "@/lib/api";
import { toast } from "sonner";

const SIDE_IMG = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwc3R1ZHklMjBhYnJvYWQlMjBwYXNzcG9ydCUyMGdyYWR1YXRpb258ZW58MHx8fHwxNzg4NDA0NTU0fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  useSEO({ title: "Log in | Training Basket", noindex: true });
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sso = {
    client_id: params.get("client_id") || "",
    redirect_uri: params.get("redirect_uri") || "",
    state: params.get("state") || "",
  };
  const ssoActive = !!(sso.client_id && sso.redirect_uri);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [mode, setMode] = useState("password");
  const [otpSent, setOtpSent] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpName, setOtpName] = useState("");
  const [otpPhone, setOtpPhone] = useState("");
  const [newUserWarn, setNewUserWarn] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const startCooldown = () => {
    setCooldown(60);
    clearInterval(timer.current);
    timer.current = setInterval(() => setCooldown((c) => (c <= 1 ? (clearInterval(timer.current), 0) : c - 1)), 1000);
  };

  const goAfterLogin = async (user) => {
    trackEvent("login", { method: mode });
    if (ssoActive) {
      try {
        const { data } = await api.get("/idp/authorize", { params: sso });
        window.location.assign(data.redirect_to);
      } catch (err) {
        setError(formatApiError(err));
      }
      return;
    }
    const next = params.get("next");
    const safeNext = next && next !== "/" && next.startsWith("/") ? next : null;
    navigate(safeNext || (user.role === "admin" ? adminPath() : "/dashboard"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      goAfterLogin(user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!form.email) { setError("Enter your email first."); return; }
    if (form.email.split("@")[0].includes("+")) { setError("Email addresses with '+alias' tags are not allowed. Please use your primary email address."); return; }
    setError("");
    setLoading(true);
    try {
      if (!newUserWarn) {
        const { data } = await api.post("/auth/check-email", { email: form.email });
        if (!data.exists) { setNewUserWarn(true); return; }
      }
      const { data } = await api.post("/auth/otp/request", { email: form.email });
      setIsNewUser(!data.exists);
      setNewUserWarn(false);
      setOtpSent(true);
      setOtp("");
      startCooldown();
      toast.success("Sign-in code sent to your email");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        email: form.email, otp, name: otpName, phone: otpPhone, referral_code: params.get("ref") || "",
        visitor_id: getVisitorId(),
      });
      setUser(data);
      toast.success(isNewUser ? "Account created — welcome!" : "Welcome back!");
      goAfterLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      toast.success("If this email exists, a reset link has been sent.");
      setForgotMode(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-10">
            <BrandLogo className="h-11 w-auto" />
          </Link>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{forgotMode ? "Reset password" : "Welcome back"}</h1>
          <p className="text-slate-500 mt-2 text-sm">{forgotMode ? "We'll email you a reset link." : "Pick up your applications right where you left them."}</p>
          {ssoActive && !forgotMode && (
            <div className="mt-4 rounded-xl bg-blue-50 border border-blue-200 px-4 py-2.5 text-xs font-semibold text-blue-800" data-testid="sso-notice">
              Sign in with your Training Basket account to continue to the partner app.
            </div>
          )}

          {!forgotMode && (
            <div className="grid grid-cols-2 gap-2 mt-8 p-1 rounded-xl bg-slate-100" data-testid="login-mode-toggle">
              <button type="button" onClick={() => { setMode("password"); setError(""); }} data-testid="mode-password"
                className={`rounded-lg py-2 text-sm font-bold transition-colors flex items-center justify-center gap-1.5 ${mode === "password" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <KeyRound className="w-3.5 h-3.5" /> Password
              </button>
              <button type="button" onClick={() => { setMode("otp"); setError(""); }} data-testid="mode-otp"
                className={`rounded-lg py-2 text-sm font-bold transition-colors flex items-center justify-center gap-1.5 ${mode === "otp" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <MailCheck className="w-3.5 h-3.5" /> Email OTP
              </button>
            </div>
          )}

          <form onSubmit={forgotMode ? forgot : mode === "password" ? submit : otpSent ? verifyOtp : sendOtp} className="mt-6 flex flex-col gap-5">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input id="login-email" type="email" required value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setNewUserWarn(false); }}
                disabled={mode === "otp" && otpSent}
                className="mt-1.5 rounded-xl py-6" data-testid="login-email-input" placeholder="you@example.com" />
            </div>
            {!forgotMode && mode === "password" && (
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input id="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1.5 rounded-xl py-6" data-testid="login-password-input" placeholder="••••••••" />
              </div>
            )}
            {!forgotMode && mode === "otp" && otpSent && (
              <>
                <div>
                  <Label htmlFor="login-otp">6-digit code</Label>
                  <Input id="login-otp" inputMode="numeric" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="mt-1.5 rounded-xl py-6 text-center text-xl tracking-[0.5em] font-mono" data-testid="login-otp-input" placeholder="••••••" />
                  <p className="text-[11px] text-slate-400 mt-1.5">Sent to {form.email} · expires in 10 minutes.</p>
                </div>
                {isNewUser && (
                  <>
                    <div>
                      <Label htmlFor="login-otp-name">Your name</Label>
                      <Input id="login-otp-name" required value={otpName} onChange={(e) => setOtpName(e.target.value)}
                        className="mt-1.5 rounded-xl py-6" data-testid="login-otp-name-input" placeholder="Full name" />
                    </div>
                    <div>
                      <Label htmlFor="login-otp-phone">Mobile number</Label>
                      <PhoneInput id="login-otp-phone" required value={otpPhone} onChange={setOtpPhone} testid="login-otp-phone-input"
                        wrapperCls="mt-1.5" inputCls="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
                      <p className="text-[11px] text-slate-400 mt-1.5">No account found for this email — we'll create one for you.</p>
                    </div>
                  </>
                )}
              </>
            )}
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="login-error">{error}</p>}
            {!forgotMode && mode === "otp" && !otpSent && newUserWarn && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="otp-new-user-warning">
                <p className="font-bold">No account found for {form.email}</p>
                <p className="text-xs mt-1">Continuing will create a <b>new account</b>. If you already have an account, double-check your email for typos above.</p>
              </div>
            )}
            <Button type="submit" disabled={loading || (mode === "otp" && otpSent && otp.length !== 6)} className={`rounded-xl py-6 font-bold ${newUserWarn ? "bg-amber-500 hover:bg-amber-600" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : forgotMode ? "Send reset link" : mode === "password" ? "Log in" : otpSent ? (isNewUser ? "Verify & create account" : "Verify & sign in") : newUserWarn ? "Yes, create a new account" : "Send sign-in code"}
            </Button>
            {!forgotMode && mode === "otp" && otpSent && (
              <div className="flex items-center justify-between text-sm -mt-2">
                <button type="button" className="text-slate-500 font-semibold" onClick={() => { setOtpSent(false); setOtp(""); setError(""); }} data-testid="otp-change-email">Change email</button>
                <button type="button" disabled={cooldown > 0 || loading} onClick={sendOtp} className="text-rose-600 font-semibold disabled:text-slate-400" data-testid="otp-resend">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            )}
          </form>

          <div className="mt-6 flex flex-col gap-2 text-sm">
            <button className="text-rose-700 font-semibold text-left" onClick={() => { setForgotMode(!forgotMode); setError(""); }} data-testid="forgot-password-link">
              {forgotMode ? "Back to login" : "Forgot password?"}
            </button>
            <p className="text-slate-500">New here? <Link to="/signup" className="text-rose-700 font-semibold" data-testid="goto-signup-link">Create an account</Link></p>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={SIDE_IMG} alt="Graduation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-slate-900/20" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-2xl font-bold leading-snug">"From shortlist to admit letter in 11 weeks — every step tracked."</p>
          <p className="text-sm text-slate-300 mt-3">Priya Nair · MS Data Science, Toronto</p>
        </div>
      </div>
    </div>
  );
}
